import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, MapPin, MessageSquare, Send } from 'lucide-react';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:info@aupairshare.co.za?subject=${encodeURIComponent(formData.subject || 'Inquiry from Website')}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`)}`;
    window.location.href = mailtoLink;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title="Contact Us"
        description="Get in touch with Au Pair Share. Questions about childcare services in Johannesburg, Pretoria, Cape Town or Durban? We're here to help."
        keywords="contact au pair share, childcare enquiry, South Africa au pair contact"
        path="/contact"
      />
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-slate-100 to-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Contact Us
            </h1>
            <p className="text-xl text-slate-600">
              Have questions? We would love to hear from you. Send us a message and we will respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <Card className="shadow-lg" data-testid="contact-form-card">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-3">
                  <MessageSquare className="w-6 h-6 text-slate-600" />
                  Send us a Message
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium text-slate-700">
                      Your Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-slate-700">
                      Email Address
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="subject" className="text-sm font-medium text-slate-700">
                      Subject
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      placeholder="What is this about?"
                      value={formData.subject}
                      onChange={handleChange}
                      data-testid="contact-subject-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-medium text-slate-700">
                      Message
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Write your message here..."
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-slate-700 hover:bg-slate-800 text-white"
                    data-testid="contact-submit-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">
                  Get in Touch
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Whether you are a family looking for flexible childcare or an au pair interested in joining our network, we are here to help. Reach out to us and we will get back to you as soon as possible.
                </p>
              </div>

              {/* Email Card */}
              <Card className="bg-slate-50 border-slate-200" data-testid="email-info-card">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-slate-700 p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Email Us</h3>
                      <a 
                        href="mailto:info@aupairshare.co.za" 
                        className="text-slate-600 hover:text-slate-900 transition-colors"
                        data-testid="contact-email-link"
                      >
                        info@aupairshare.co.za
                      </a>
                      <p className="text-sm text-slate-500 mt-2">
                        We typically respond within 24-48 hours
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Locations Card */}
              <Card className="bg-slate-50 border-slate-200" data-testid="locations-info-card">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-slate-700 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Service Areas</h3>
                      <p className="text-slate-600">
                        We currently serve families in:
                      </p>
                      <ul className="text-slate-600 mt-2 space-y-1">
                        <li>• Johannesburg</li>
                        <li>• Pretoria</li>
                        <li>• Cape Town</li>
                        <li>• Durban</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* FAQ Note */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-2">Looking for quick answers?</h3>
                <p className="text-slate-600 text-sm">
                  Check out our Safety page for information about our vetting process, or visit the For Families and For Au Pairs pages for detailed information about how Au Pair Share works.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
